import z from 'zod';
import { Cash } from './cash';

enum OrderTypeEnum {
  OMS = 0,
  Offline = 1,
  AlgoOrder = 2,
  Conditional = 3,
}

enum OrderSideEnum {
  Buy = 1,
  Sell = 2,
}

const TSE = z.object({
  code: z.string().nullable(),
  title: z.string().nullable(),
});

const FUT = z.object({
  code: z.string().nullable(),
  title: z.string().nullable(),
});

const Calendar = z.object({
  date: z.string(),
  fut: FUT.nullable(),
  persianDate: z.string().nullable(),
  time: z.string().nullable(),
  tse: TSE.nullable(),
});

const SummarizedOrder = z.object({
  tradeDate: z.string(),
  orderSideId: z.nativeEnum(OrderSideEnum),
  instrumentId: z.number(),
  instrumentIdentification: z.string().nullable(),
  instrumentTitle: z.string().nullable(),
  instrumentCode: z.string().nullable(),
  averagePrice: z.number(),
  quantity: z.number(),
  wage: z.number().nullable(),
  tradeCount: z.number(),
  instrumentTypeCode: z.string().nullable(),
  sectorCode: z.string().nullable(),
  subSectorCode: z.string().nullable(),
});

const SummarizedOrders = z.array(SummarizedOrder);

const OrderItem = z.object({
  hostOrderNumber: z.number(),
  orderNumber: z.string().nullable(),
  instrumentName: z.string().nullable(),
  instrumentTitle: z.string().nullable(),
  instrumentState: z.string().nullable(),
  entryPersianDate: z.string().nullable(),
  orderSideId: z.nativeEnum(OrderSideEnum),
  orderSideName: z.string().nullable(),
  orderState: z.string().nullable(),
  orderStateEnglishTitle: z.string().nullable(),
  entryTime: z.string().nullable(),
  errorDescription: z.string().nullable(),
  instrumentIdentification: z.string().nullable(),
  personageId: z.number(),
  investorTitle: z.string().nullable(),
  accountRouteType: z.number(),
  accountRouteTypeTitle: z.string().nullable(),
  parentExecutedQuantity: z.number(),
  validityPersianDate: z.string().nullable(),
  validityDate: z.string().nullable(),
  validityType: z.string().nullable(),
  validityTypeId: z.number(),
  dateOfEvent: z.string().nullable(),
  orderTypeTitle: z.string().nullable(),
  investorBourseCodeId: z.number(),
  entryDate: z.string(),
  orderStateId: z.number(),
  orderEntryDeviceTypeId: z.number(),
  orderId: z.string(),
  quantity: z.number(),
  price: z.number(),
  remainingQuantity: z.number(),
  blokedValue: z.number().nullable(),
  executedQuantity: z.number(),
  canceledQuantity: z.number(),
  personageTitle: z.string().nullable(),
  validityEnglishTitle: z.string().nullable(),
  sectorCode: z.string().nullable(),
  subSectorCode: z.string().nullable(),
  instrumentTypeCode: z.string().nullable(),
  exchangeCode: z.string().nullable(),
  referencePriceVariationPercentage: z.number(),
  lastTradePrice: z.number(),
  orderType: z.nativeEnum(OrderTypeEnum),
  upperThreshold: z.number().nullable(),
  lowerThreshold: z.number().nullable(),
  investValue: z.number().nullable(),
  executedValue: z.number().nullable(),
  orderExecutionType: z.string().nullable(),
  isAlgorithm: z.boolean().nullish(),
});

const OrderHistoryItem = z.object({
  orderId: z.string(),
  orderName: z.string().nullable(),
  hostOrderNumber: z.number(),
  instrumentName: z.string().nullable(),
  instrumentTitle: z.string().nullable(),
  orderSideId: z.nativeEnum(OrderSideEnum),
  orderSide: z.string().nullable(),
  orderStateId: z.number(),
  orderState: z.string().nullable(),
  orderStateEnglishTitle: z.string().nullable(),
  remainingQuantity: z.number(),
  blokedValue: z.number(),
  quantity: z.number(),
  price: z.number(),
  entryDate: z.string(),
  errorDescription: z.string().nullable(),
  instrumentIdentification: z.string().nullable(),
  investorTitle: z.string().nullable(),
  executedQuantity: z.number(),
  canceledQuantity: z.number(),
  isin: z.string().nullable(),
  instrumentTypeCode: z.string().nullable(),
  sectorCode: z.string().nullable(),
  subSectorCode: z.string().nullable(),
  validityTypeId: z.number(),
  validityDate: z.string(),
  validityTypeTitle: z.string().nullable(),
  exchangeCode: z.string().nullable(),
  referencePriceVariationPercentage: z.number(),
  lastTradePrice: z.number(),
  instrumentStateCode: z.string().nullable(),
  instrumentTradingState: z.number(),
  instrumentGroupId: z.string().nullable(),
  closingPrice: z.number(),
  closingPriceVariationPercentage: z.number(),
  orderType: z.nativeEnum(OrderTypeEnum),
  investValue: z.number().nullable(),
  executedValue: z.number().nullable(),
  legacyOrderState: z
    .object({
      orderStateId: z.number(),
      orderState: z.string().nullable(),
      orderStateEnglishTitle: z.string().nullable(),
    })
    .nullable(),
  newOrderState: z
    .object({
      orderStateId: z.number(),
      orderState: z.string().nullable(),
      orderStateEnglishTitle: z.string().nullable(),
    })
    .nullable(),
  orderExecutionType: z.string().nullable(),
});

const Orders = z.object({
  items: OrderItem.array().nullable(),
  totalCount: z.number(),
});

const OrdersHistoryQuery = z.object({
  items: OrderHistoryItem.array().nullable(),
  totalCount: z.number(),
});

const CreateOrderItem = z.object({
  orderId: z.string(),
  instrumentIsin: z.string().nullable(),
  remainingQuantity: z.number(),
  investorBourseCodeId: z.number(),
  investorTitle: z.string().nullable(),
  investorBourseCode: z.string().nullable(),
  lastPrice: z.number().nullable(),
  accountRouteTypeId: z.number(),
  quantity: z.number(),
  orderNumber: z.string().nullable(),
  userName: z.string().nullable(),
  personageId: z.number(),
  instrumentName: z.string().nullable(),
  orderStateId: z.number(),
  orderState: z.string().nullable(),
  orderSideId: z.nativeEnum(OrderSideEnum),
  orderSide: z.string().nullable(),
  orderStateEnglishTitle: z.string().nullable(),
  orderEntryPersianDate: z.string(),
  time: z.string().nullable(),
  parentExecutedQuantity: z.number(),
  validityTypeTitle: z.string().nullable(),
  price: z.number(),
  userOrderEntryId: z.number(),
  canceledQuantity: z.string(),
  entryDate: z.string(),
});

const CreateOrderResponse = z.object({
  result: z.object({
    orderId: z.string(),
    ruleAction: z.string().nullable(),
    ruleConditionMessageList: z.string().array().nullable(),
    busyForResponse: z.boolean(),
    accountState: Cash,
    orderInfo: CreateOrderItem.nullable(),
  }),
});

const CreateOfflineOrderItem = z.object({
  instrumentIdentification: z.string().nullable(),
  orderSide: z.number(),
  quantity: z.number(),
  price: z.number(),
  orderExecutionType: z.string().nullable(),
});

const OrderPosition = z.object({
  waitingVolume: z.number(),
  positionInSamePrice: z.number(),
  position: z.number(),
  totalWaitingTradeValue: z.number(),
});

const TradeItem = z.object({
  id: z.number(),
  tradeId: z.number(),
  orderId: z.string(),
  investorBourseCodeId: z.number(),
  instrumentId: z.number(),
  counterpartBrokerId: z.number(),
  tradeSidedId: z.number(),
  tradeStateId: z.number(),
  tradeTypeId: z.number(),
  traderId: z.number(),
  customerStationId: z.number(),
  personageId: z.number(),
  traderPersonageId: z.number(),
  traderPersonageTypeId: z.number(),
  personageTypeId: z.number(),
  brokerBranchId: z.number(),
  tradeDateTime: z.date(),
  dateOfEvent: z.date(),
  quantity: z.number(),
  remainingQuantity: z.number(),
  price: z.number(),
  importAveragePrice: z.number(),
  tradeNumber: z.string().nullable(),
  deductionValue: z.number(),
  personageTitle: z.string().nullable(),
  personageNationalCode: z.string().nullable(),
  dealerCreditUsed: z.number(),
  investorBourseCode: z.string().nullable(),
  investorBourseCodeIsin: z.string().nullable(),
  investorBourseCodeBim: z.string().nullable(),
  instrumentCode: z.string().nullable(),
  instrumentTitle: z.string().nullable(),
  instrumentName: z.string().nullable(),
  instrumentCompanyTitle: z.string().nullable(),
  instrumentIsin: z.string().nullable(),
  instrumentEnglishTitle: z.string().nullable(),
  tradeSideTitle: z.string().nullable(),
  tradeSideEnglishTitle: z.string().nullable(),
  tradeStateTitle: z.string().nullable(),
  tradeStateEnglishTitle: z.string().nullable(),
  tradeTypeTitle: z.string().nullable(),
  tradeTypeEnglishTitle: z.string().nullable(),
  traderCode: z.string().nullable(),
  traderPersonageTitle: z.string().nullable(),
  traderPersonageEnglishTitle: z.string().nullable(),
  traderPersonageTypeTitle: z.string().nullable(),
  traderPersonageTypeEnglishTitle: z.string().nullable(),
  traderStationTitle: z.string().nullable(),
  customerStationCode: z.string().nullable(),
  customerStationTitle: z.string().nullable(),
  brokerBranchTitle: z.string().nullable(),
  entryUserName: z.string().nullable(),
  sectorCode: z.string().nullable(),
  subSectorCode: z.string().nullable(),
  instrumentTypeCode: z.string().nullable(),
  value: z.number().nullish(),
});

const Trades = z.object({
  totalCount: z.number(),
  items: TradeItem.array().nullable(),
});

const FileReportResult = z.object({
  content: z.string().nullable(),
  name: z.string().nullable(),
  contentType: z.string().nullable(),
});

const OfflineStatus = z.object({
  isOffline: z.boolean(),
});

const OMSStatus = z.object({
  isMigrated: z.boolean(),
});

const CreateConditionalOrderRequest = z.object({
  instrumentIdentification: z.string().nullable(),
  orderSide: z.number(),
  quantity: z.number(),
  value: z.number(),
  upperThreshold: z.number().nullable(),
  lowerThreshold: z.number().nullable(),
  orderExecutionType: z.string().nullable(),
  clientTag: z.string().nullable(),
  validityDate: z.date().nullable(),
  bypassSellQuantity: z.boolean().nullable(),
});

type Calendar = z.infer<typeof Calendar> | null | undefined;
type CreateOrderResponse = z.infer<typeof CreateOrderResponse> | null;
type OrderItem = z.infer<typeof OrderItem>;
type OrderHistoryItem = z.infer<typeof OrderHistoryItem>;
type Orders = z.infer<typeof Orders> | null | undefined;
type OrdersHistoryQuery = z.infer<typeof OrdersHistoryQuery>;
type CreateOrderItem = z.infer<typeof CreateOrderItem> | null | undefined;
type OrderPosition = z.infer<typeof OrderPosition>;
type TradeItem = z.infer<typeof TradeItem>;
type Trades = z.infer<typeof Trades> | null | undefined;
type FileReportResult = z.infer<typeof FileReportResult>;
type CreateOfflineOrderItem = z.infer<typeof CreateOfflineOrderItem>;
type OfflineStatus = z.infer<typeof OfflineStatus>;
type SummarizedOrder = z.infer<typeof SummarizedOrder>;
type SummarizedOrders = z.infer<typeof SummarizedOrders>;
type CreateConditionalOrderRequest = z.infer<
  typeof CreateConditionalOrderRequest
>;
type OMSStatus = z.infer<typeof OMSStatus>;

export {
  Calendar,
  CreateOrderResponse,
  OrderItem,
  Orders,
  CreateOrderItem,
  OrderPosition,
  Trades,
  TradeItem,
  OrdersHistoryQuery,
  OrderHistoryItem,
  FileReportResult,
  CreateOfflineOrderItem,
  OfflineStatus,
  SummarizedOrder,
  SummarizedOrders,
  OrderTypeEnum,
  OrderSideEnum,
  CreateConditionalOrderRequest,
  OMSStatus,
};
